"use client"

import * as React from 'react'
import { useRouter, useParams } from 'next/navigation'
import { Page, PageBody } from '@open-mercato/ui/backend/Page'
import { CrudForm, type CrudField, type CrudFormGroup } from '@open-mercato/ui/backend/CrudForm'
import { updateCrud, deleteCrud } from '@open-mercato/ui/backend/utils/crud'
import { flash } from '@open-mercato/ui/backend/FlashMessages'
import { LoadingMessage, ErrorMessage } from '@open-mercato/ui/backend/detail'
import { apiCall } from '@open-mercato/ui/backend/utils/apiCall'
import { useOrganizationScopeVersion } from '@open-mercato/shared/lib/frontend/useOrganizationScope'
import { useT } from '@open-mercato/shared/lib/i18n/context'

type AdapterData = {
  id: string
  adapter_type: string
  name: string
  config: Record<string, unknown> | null
  polling_interval_seconds: number
  is_active: boolean
  last_poll_at: string | null
  last_error: string | null
  created_at: string
  updated_at: string
}

const ADAPTER_TYPE_OPTIONS = [
  { value: 'GBOX', label: 'G-Box' },
  { value: 'TRIMBLE', label: 'Trimble' },
  { value: 'MQTT_GENERIC', label: 'MQTT Generic' },
  { value: 'WEBHOOK_GENERIC', label: 'Webhook Generic' },
  { value: 'MANUAL', label: 'Manual' },
]

type RouteParams = {
  id?: string | string[]
  slug?: string[]
}

function readRouteId(params?: RouteParams | null): string {
  if (!params) return ''
  if (typeof params.id === 'string') return params.id
  if (Array.isArray(params.id)) return params.id[0] ?? ''
  if (Array.isArray(params.slug)) return params.slug.at(-1) ?? ''
  return ''
}

export default function AdapterDetailPage({ params }: { params?: RouteParams }) {
  const t = useT()
  const router = useRouter()
  const routeParams = useParams()
  const id = readRouteId(params) || readRouteId(routeParams as RouteParams | undefined)
  const scopeVersion = useOrganizationScopeVersion()

  const [adapter, setAdapter] = React.useState<AdapterData | null>(null)
  const [isLoading, setIsLoading] = React.useState(true)
  const [loadError, setLoadError] = React.useState<string | null>(null)

  React.useEffect(() => {
    if (!id) {
      setAdapter(null)
      setLoadError(t('rail_tracking.adapters.detail.error.load', 'Adapter not found'))
      setIsLoading(false)
      return
    }
    let cancelled = false

    async function load() {
      setIsLoading(true)
      setLoadError(null)
      try {
        const fallback = { items: [] as AdapterData[] }
        const res = await apiCall<{ items: AdapterData[]; error?: string }>(
          `/api/rail_tracking/adapters?ids=${encodeURIComponent(id)}&pageSize=1`,
          undefined,
          { fallback },
        )
        if (cancelled) return

        if (!res.ok) {
          setAdapter(null)
          setLoadError(
            typeof res.result?.error === 'string'
              ? res.result.error
              : t('rail_tracking.adapters.detail.error.load', 'Failed to load adapter'),
          )
          return
        }

        const item = Array.isArray(res.result?.items) ? res.result.items[0] : null
        if (item) {
          setAdapter(item)
          return
        }

        setAdapter(null)
        setLoadError(t('rail_tracking.adapters.detail.error.load', 'Adapter not found'))
      } catch (error) {
        if (cancelled) return
        setAdapter(null)
        setLoadError(
          error instanceof Error && error.message
            ? error.message
            : t('rail_tracking.adapters.detail.error.load', 'Failed to load adapter'),
        )
      } finally {
        if (!cancelled) {
          setIsLoading(false)
        }
      }
    }

    load()
    return () => { cancelled = true }
  }, [id, scopeVersion, t])

  const fields = React.useMemo<CrudField[]>(() => [
    {
      id: 'name',
      label: t('rail_tracking.adapters.fields.name', 'Name'),
      type: 'text',
      required: true,
      placeholder: 'Locomotive GPS Tracker #1',
    },
    {
      id: 'adapterType',
      label: t('rail_tracking.adapters.fields.adapterType', 'Adapter Type'),
      type: 'select',
      required: true,
      options: ADAPTER_TYPE_OPTIONS,
    },
    {
      id: 'pollingIntervalSeconds',
      label: t('rail_tracking.adapters.fields.pollingInterval', 'Polling Interval (seconds)'),
      type: 'number',
      placeholder: '60',
    },
    {
      id: 'isActive',
      label: t('rail_tracking.adapters.fields.isActive', 'Active'),
      type: 'checkbox',
    },
  ], [t])

  const groups = React.useMemo<CrudFormGroup[]>(() => [
    {
      id: 'general',
      title: t('rail_tracking.adapters.groups.general', 'General'),
      fields: ['name', 'adapterType'],
    },
    {
      id: 'configuration',
      title: t('rail_tracking.adapters.groups.configuration', 'Configuration'),
      fields: ['pollingIntervalSeconds', 'isActive'],
    },
  ], [t])

  if (isLoading) return <LoadingMessage label="" />
  if (loadError || !adapter) return <ErrorMessage label={loadError ?? t('rail_tracking.adapters.detail.error.load', 'Adapter not found')} />

  const initialValues = {
    name: adapter.name,
    adapterType: adapter.adapter_type,
    pollingIntervalSeconds: adapter.polling_interval_seconds,
    isActive: adapter.is_active,
  }

  return (
    <Page>
      <PageBody>
        <CrudForm
          title={t('rail_tracking.adapters.detail.title', 'Edit GPS Adapter')}
          backHref="/backend/rail-tracking"
          fields={fields}
          groups={groups}
          initialValues={initialValues}
          submitLabel={t('rail_tracking.adapters.detail.submit', 'Save Changes')}
          cancelHref="/backend/rail-tracking"
          onDelete={async () => {
            await deleteCrud('rail_tracking/adapters', { id: adapter.id })
            flash(t('rail_tracking.adapters.flash.deleted', 'GPS adapter deleted'), 'success')
            router.push('/backend/rail-tracking')
          }}
          deleteRedirect="/backend/rail-tracking"
          onSubmit={async (values) => {
            const payload = {
              id: adapter.id,
              ...values,
              pollingIntervalSeconds: values.pollingIntervalSeconds ? Number(values.pollingIntervalSeconds) : 60,
            }
            await updateCrud('rail_tracking/adapters', payload)
            flash(t('rail_tracking.adapters.flash.updated', 'GPS adapter updated'), 'success')
            router.push('/backend/rail-tracking')
          }}
        />
      </PageBody>
    </Page>
  )
}
